/**
 * Homepage Hero Banner — fade slider with device-aware lazy media loading.
 * Only the active slide + current viewport (desktop/mobile) media is loaded.
 */
(function () {
  'use strict';

  var instances = new WeakMap();

  function HeroBannerSlider(root) {
    this.root = root;
    this.track = root.querySelector('.hhb__slides');
    this.slides = this.track ? Array.prototype.slice.call(this.track.querySelectorAll('.hhb__slide')) : [];
    this.dotsWrap = root.querySelector('[data-hhb-dots]');
    this.prevBtn = root.querySelector('[data-hhb-prev]');
    this.nextBtn = root.querySelector('[data-hhb-next]');
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.speed = parseInt(root.getAttribute('data-speed'), 10) || 7000;
    this.current = 0;
    this.timer = null;
    this.desktopQuery = window.matchMedia('(min-width: 769px)');

    this.onResize = this.onResize.bind(this);
    this.onDesktopChange = this.onDesktopChange.bind(this);

    if (this.slides.length <= 1) {
      if (this.slides[0]) this.slides[0].classList.add('is-active');
      this.syncMedia();
      return;
    }

    this.buildDots();
    this.bindEvents();
    this.goTo(0, false);
    this.startAutoplay();
  }

  HeroBannerSlider.prototype.isDesktop = function () {
    return this.desktopQuery.matches;
  };

  HeroBannerSlider.prototype.activateMedia = function (device) {
    var medias = device.querySelectorAll('[data-hhb-media]');
    medias.forEach(function (el) {
      if (el.tagName === 'VIDEO') {
        var sources = el.querySelectorAll('source[data-hhb-src]');
        var changed = false;
        sources.forEach(function (source) {
          var url = source.getAttribute('data-hhb-src');
          if (url && source.getAttribute('src') !== url) {
            source.setAttribute('src', url);
            changed = true;
          }
        });
        if (changed || !el.getAttribute('src')) {
          el.load();
        }
      } else if (el.tagName === 'IMG') {
        var src = el.getAttribute('data-hhb-src');
        var srcset = el.getAttribute('data-hhb-srcset');
        if (src && el.getAttribute('src') !== src) {
          el.setAttribute('src', src);
        }
        if (srcset && el.getAttribute('srcset') !== srcset) {
          el.setAttribute('srcset', srcset);
        }
      }
    });
  };

  HeroBannerSlider.prototype.deactivateMedia = function (device) {
    device.querySelectorAll('video').forEach(function (video) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(function (source) {
          source.removeAttribute('src');
        });
        video.load();
      } catch (e) {}
    });
  };

  HeroBannerSlider.prototype.syncMedia = function () {
    var isDesktop = this.isDesktop();
    var self = this;

    this.root.querySelectorAll('.hhb__device').forEach(function (device) {
      var isDesktopDevice = device.getAttribute('data-hhb-device') === 'desktop';
      var matchesViewport = isDesktop ? isDesktopDevice : !isDesktopDevice;
      var slide = device.closest('.hhb__slide');
      var isActiveSlide = slide && slide.classList.contains('is-active');

      if (matchesViewport && isActiveSlide) {
        self.activateMedia(device);
        device.querySelectorAll('video[data-hhb-autoplay]').forEach(function (video) {
          var playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {});
          }
        });
      } else {
        self.deactivateMedia(device);
      }
    });
  };

  HeroBannerSlider.prototype.buildDots = function () {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    var self = this;
    this.dots = this.slides.map(function (_, index) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hhb__dot';
      btn.setAttribute('aria-label', 'Go to slide ' + (index + 1));
      btn.addEventListener('click', function () {
        self.goTo(index, true);
        self.startAutoplay();
      });
      self.dotsWrap.appendChild(btn);
      return btn;
    });
  };

  HeroBannerSlider.prototype.bindEvents = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.prev();
        self.startAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.next();
        self.startAutoplay();
      });
    }

    this.root.addEventListener('mouseenter', function () {
      self.stopAutoplay();
    });

    this.root.addEventListener('mouseleave', function () {
      self.startAutoplay();
    });

    this.root.addEventListener('focusin', function () {
      self.stopAutoplay();
    });

    this.root.addEventListener('focusout', function () {
      self.startAutoplay();
    });

    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onDesktopChange);
    } else if (this.desktopQuery.addListener) {
      this.desktopQuery.addListener(this.onDesktopChange);
    }
    window.addEventListener('resize', this.onResize);
  };

  HeroBannerSlider.prototype.onDesktopChange = function () {
    this.syncMedia();
  };

  HeroBannerSlider.prototype.onResize = function () {
    this.syncMedia();
  };

  HeroBannerSlider.prototype.goTo = function (index) {
    if (!this.slides.length) return;

    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;

    this.current = index;

    this.slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    if (this.dots) {
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    this.syncMedia();
  };

  HeroBannerSlider.prototype.next = function () {
    this.goTo(this.current + 1);
  };

  HeroBannerSlider.prototype.prev = function () {
    this.goTo(this.current - 1);
  };

  HeroBannerSlider.prototype.startAutoplay = function () {
    var self = this;
    this.stopAutoplay();
    if (!this.autoplay || this.slides.length <= 1) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.timer = window.setInterval(function () {
      self.next();
    }, this.speed);
  };

  HeroBannerSlider.prototype.stopAutoplay = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  HeroBannerSlider.prototype.goToBlock = function (blockId) {
    for (var i = 0; i < this.slides.length; i += 1) {
      if (this.slides[i].getAttribute('data-slide-id') === blockId) {
        this.goTo(i);
        this.stopAutoplay();
        break;
      }
    }
  };

  HeroBannerSlider.prototype.destroy = function () {
    this.stopAutoplay();
    window.removeEventListener('resize', this.onResize);
    if (this.desktopQuery.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.onDesktopChange);
    } else if (this.desktopQuery.removeListener) {
      this.desktopQuery.removeListener(this.onDesktopChange);
    }
  };

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    var slider = new HeroBannerSlider(root);
    instances.set(root, slider);
  }

  function boot(scope) {
    var context = scope || document;
    context.querySelectorAll('[data-hhb-slider]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-hhb-slider]').forEach(function (root) {
      if (instances.has(root)) {
        instances.get(root).destroy();
        instances.delete(root);
      }
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = document.getElementById('HhbSection-' + event.detail.sectionId);
    if (!root || !instances.has(root)) return;
    instances.get(root).goToBlock(event.detail.blockId);
  });
})();
